import { Component } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';

import {
    NUMBER_OF_PRODUCT_IMPRESSIONS,
    trackEcommerceProductImpressions,
    trackEcommerceProductClick,
} from '../../utils/tracking/searchBrowse/ecommerceTracking';
import { getUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';

class SbSharedItemTrackingProviderComponent extends Component {
    constructor() {
        super();

        this.fireItemImpressionTracking = this.fireItemImpressionTracking.bind(this);
        this.fireItemClickTracking = this.fireItemClickTracking.bind(this);
        this.trackedItemImpressionIndexes = [];
        this.attemptedItemImpressionIndexes = [];
    }

    componentDidMount() {
        this.fireItemImpressionTracking(0);
    }

    componentDidUpdate(prevProps) {
        if (this.props.itemSearch.displayUriRef !== prevProps.itemSearch.displayUriRef) {
            /**
             * Assume the page changed and reset tracking data so that we're able to fire tracking
             * for the new set of items.
             */
            this.trackedItemImpressionIndexes = [];
            this.fireItemImpressionTracking(0);
        }

        if (this.regionalInfoAvailable() && this.attemptedItemImpressionIndexes.length !== 0) {
            this.attemptedItemImpressionIndexes.forEach(attemptedItemImpressionIndex => {
                this.fireItemImpressionTracking(attemptedItemImpressionIndex);
            });
            this.attemptedItemImpressionIndexes = [];
        }
    }

    getRegions() {
        return (
            getUserSessionRegions() || this.props.viewer?.regionalInfo?.[0]?.regionsByZipCode || []
        );
    }

    regionalInfoAvailable() {
        return !!(getUserSessionRegions() || this.props.viewer?.regionalInfo);
    }

    trackProductImpression(startIndex) {
        const { searchCorrectionTerm, itemSearch, isRelatedSearchItems } = this.props;
        const { searchTerm, pageType, edges: items } = itemSearch || {};
        const userRegions = this.getRegions();

        trackEcommerceProductImpressions({
            items,
            pageType,
            searchTerm,
            startIndex,
            searchCorrectionTerm,
            userRegions,
            isRelatedSearchItems,
        });
    }

    fireItemImpressionTracking(index) {
        /**
         * Group items from index 0 to 19 to group with the number `0`, from 20 to 39 to group
         * 1 and so on.
         */
        const impressionGroup = Math.floor(index / NUMBER_OF_PRODUCT_IMPRESSIONS);

        if (!this.regionalInfoAvailable()) {
            this.attemptedItemImpressionIndexes.push(impressionGroup);
        } else if (!this.trackedItemImpressionIndexes.includes(impressionGroup)) {
            /**
             * Very important to track indexes that have already fired - `react-lazy-load` can fire
             * `contentVisible` handler multiple times for the same item.
             */
            this.trackProductImpression(impressionGroup * NUMBER_OF_PRODUCT_IMPRESSIONS);
            this.trackedItemImpressionIndexes.push(impressionGroup);
        }
    }

    fireItemClickTracking(index) {
        const { searchCorrectionTerm, itemSearch } = this.props;
        const { searchTerm, pageType, edges: items } = itemSearch || {};
        const itemNode = items?.[index]?.node;
        const item = itemNode?.item || itemNode;
        const userRegions = this.getRegions();

        trackEcommerceProductClick({
            item,
            index,
            pageType,
            searchTerm,
            searchCorrectionTerm,
            userRegions,
        });
    }

    render() {
        return this.props.children({
            fireItemImpressionTracking: this.fireItemImpressionTracking,
            fireItemClickTracking: this.fireItemClickTracking,
        });
    }
}

SbSharedItemTrackingProviderComponent.propTypes = {
    children: PropTypes.func,
    searchCorrectionTerm: PropTypes.string,
    itemSearch: PropTypes.object.isRequired,
    viewer: PropTypes.object,
    isRelatedSearchItems: PropTypes.string,
};

/*
  Fields necessary for ecommerce tracking...
  serviceId
  title
  seller { serviceId }
  categoryPath
  contemporaryTrackingString
  displayInfo(buyerId: $userId) @include(if: $isClient) {
    pricing {
        showNet
        showSale
    }
  }
  pricing {
    amount {
        convertedAmountList {
            amount
            currency
        }
    }
    salePrice {
        convertedAmountList {
            amount
            currency
        }
    }
    netPrice {
        convertedAmountList {
            amount
            currency
        }
    }
  }
*/
export const SbSharedItemTrackingProvider = createFragmentContainer(
    SbSharedItemTrackingProviderComponent,
    {
        itemSearch: graphql`
            fragment SbSharedItemTrackingProvider_itemSearch on ItemSearchQueryConnection {
                pageType
                searchTerm
                displayUriRef
                edges {
                    node {
                        item {
                            serviceId
                            title
                            categoryPath
                            browseUrl
                            categoryCode
                            contemporaryTrackingString
                            seller {
                                serviceId
                            }
                            displayPriceTracking: displayPrice(
                                page: searchAndBrowse
                                isTrade: $isTrade
                            ) {
                                convertedAmountList {
                                    amount
                                    currency
                                }
                                amountType
                                textType
                            }
                            isAuctionEnabled
                            ...freeShippingHelpers_item
                        }
                    }
                }
            }
        `,
        viewer: graphql`
            fragment SbSharedItemTrackingProvider_viewer on Viewer {
                regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
                    @include(if: $fetchRegionalInfo) {
                    regionsByZipCode {
                        displayName
                    }
                }
            }
        `,
    }
);
